import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, formatDateTime, BRAND } from "@/lib/api";
import { Search, Plus, BellRing } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = ["", "Received", "Processing", "Dry Cleaning", "Ironing", "Ready", "Out for Delivery", "Delivered"];

const Badge = ({ s }) => {
  const map = { Delivered: "qw-badge-green", Ready: "qw-badge-green", Received: "qw-badge-blue", "Out for Delivery": "qw-badge-blue", Processing: "qw-badge-orange", "Dry Cleaning": "qw-badge-orange", Ironing: "qw-badge-orange" };
  return <span className={map[s] || "qw-badge-slate"}>{s}</span>;
};

const buildReady = (tmpl, b) => {
  const map = {
    customer_name: b.customer_name,
    bill_number: b.bill_number,
    grand_total: b.grand_total,
    balance: b.balance,
    delivery_date: b.delivery_date,
    order_date: b.order_date,
    public_link: `${window.location.origin}/bill/${b.bill_number}`,
    business_phone: BRAND.phone,
  };
  let out = tmpl || "";
  Object.keys(map).forEach((k) => { out = out.replaceAll(`{${k}}`, map[k]); });
  return out;
};

const Bills = () => {
  const [bills, setBills] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [readyTmpl, setReadyTmpl] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    const { data } = await api.get(`/bills?${params.toString()}`);
    setBills(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { api.get("/settings/messages").then((r) => setReadyTmpl(r.data.templates.ready_for_delivery)).catch(() => {}); }, []);

  const sendReady = (b, e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = buildReady(readyTmpl, b);
    const mobile = b.mobile.replace(/\D/g, "");
    const full = mobile.length === 10 ? `91${mobile}` : mobile;
    window.open(`https://wa.me/${full}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success(`Ready reminder for ${b.customer_name} opened in WhatsApp`);
  };

  return (
    <div className="space-y-6" data-testid="bills-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>All Bills</h1>
          <p className="text-sm text-slate-500">Search, filter and manage all your bills.</p>
        </div>
        <Link to="/admin/bills/new" className="qw-btn-primary" data-testid="bills-new-btn"><Plus size={16}/> New Bill</Link>
      </div>

      <div className="qw-dash-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by bill no, mobile or name…" className="qw-input pl-10" data-testid="bills-search-input"/>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="qw-input w-52" data-testid="bills-status-filter">
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <button onClick={load} className="qw-btn-primary" data-testid="bills-search-btn">Search</button>
      </div>

      <div className="qw-dash-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left p-4">Bill No</th>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Delivery</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Payment</th>
              <th className="text-right p-4">Amount</th>
              <th className="p-4"/>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <tr key={i}><td colSpan="8" className="p-4"><div className="h-8 bg-slate-100 rounded animate-pulse"/></td></tr>)
            ) : bills.length === 0 ? (
              <tr><td colSpan="8" className="p-12 text-center text-slate-500" data-testid="bills-empty">
                <div className="text-lg font-semibold text-slate-700">No bills yet</div>
                <div className="text-sm mt-1 mb-4">Create your first bill to get started.</div>
                <Link to="/admin/bills/new" className="qw-btn-primary">Create Bill</Link>
              </td></tr>
            ) : bills.map((b) => (
              <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bill-row-${b.bill_number}`}>
                <td className="p-4"><Link to={`/admin/bills/${b.id}`} className="font-semibold text-slate-900 hover:text-blue-600">{b.bill_number}</Link></td>
                <td className="p-4 text-slate-600">{formatDateTime(b.created_at)}</td>
                <td className="p-4"><div className="text-slate-900">{b.customer_name}</div><div className="text-xs text-slate-500">{b.mobile}</div></td>
                <td className="p-4 text-slate-600">{b.delivery_date}</td>
                <td className="p-4"><Badge s={b.status}/></td>
                <td className="p-4"><span className="qw-badge-slate">{b.payment_mode}</span></td>
                <td className="p-4 text-right font-semibold">{formatINR(b.grand_total)}</td>
                <td className="p-4 text-right">
                  {(b.status === "Ready" || b.status === "Out for Delivery") && (
                    <button onClick={(e) => sendReady(b, e)} title="Send Ready Reminder on WhatsApp" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold transition-colors" data-testid={`bill-row-ready-${b.bill_number}`}>
                      <BellRing size={12}/>Ready
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Bills;
